/// <reference lib="dom" />
import * as d3 from "d3";
import type { ChartLayer } from "../core/chart.ts";
import { Single } from "./shared.ts";

export interface SamplesLayer extends ChartLayer {
  set(samples: ArrayLike<number> | null, rate: number): void;
}

interface MinMaxBin {
  x: number;
  lo: number;
  hi: number;
}

export interface SamplesLayerOpts {
  id?: string;
  color?: string;
}

export function SamplesLayer(opts: SamplesLayerOpts = {}): SamplesLayer {
  let samples: ArrayLike<number> | null = null;
  let rate = 1;
  const area = d3.area<MinMaxBin>().x((b) => b.x);
  const line = d3.line<[number, number]>();

  return {
    id: opts.id ?? "samples",
    clip: true,
    set(next, nextRate) {
      samples = next;
      rate = nextRate;
    },
    render(root, ctx) {
      const color = opts.color ?? ctx.theme.series;
      const path = Single<SVGPathElement>(root, "path", "samples")
        .attr("stroke", color)
        .attr("stroke-width", 1)
        .attr("stroke-linejoin", "round")
        .style("pointer-events", "none");
      if (!samples || samples.length === 0) {
        path.attr("d", null);
        return;
      }
      const { x, y } = ctx;
      const { inner } = ctx.frame;
      const count = samples.length;
      const [t0, t1] = x.domain();
      const cols = Math.max(1, Math.round(inner.width));
      const perCol = ((t1 - t0) * rate) / cols;

      if (perCol > 1) {
        const bins: MinMaxBin[] = [];
        for (let col = 0; col < cols; col++) {
          const from = Math.max(0, Math.floor((t0 + (col / cols) * (t1 - t0)) * rate));
          const to = Math.min(
            count,
            Math.max(from + 1, Math.floor((t0 + ((col + 1) / cols) * (t1 - t0)) * rate)),
          );
          if (from >= count || to <= 0) continue;
          let lo = Infinity;
          let hi = -Infinity;
          for (let idx = from; idx < to; idx++) {
            const v = samples[idx];
            if (v < lo) lo = v;
            if (v > hi) hi = v;
          }
          bins.push({ x: inner.left + col + 0.5, lo, hi });
        }
        area.y0((b) => y(b.lo)).y1((b) => y(b.hi));
        path.attr("fill", color).attr("fill-opacity", 0.35).attr("d", area(bins));
        return;
      }

      const from = Math.max(0, Math.floor(t0 * rate) - 1);
      const to = Math.min(count, Math.ceil(t1 * rate) + 2);
      const points: [number, number][] = [];
      for (let idx = from; idx < to; idx++) points.push([x(idx / rate), y(samples[idx])]);
      path
        .attr("fill", "none")
        .attr("stroke-width", ctx.theme.strokeWidth)
        .attr("d", line(points));
    },
  };
}
